import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail } from 'lucide-react';

// Founders — Scott & Laura Pelley. Real photos supplied by client (June 2026).
const SCOTT = {
  id: 'scott-pelley',
  name: 'Scott Pelley',
  title: 'President & CEO',
  email: 'scott@ozarkmtnco.com',
  photo: '/team/scott-portrait.jpg?v=2',
  bio: 'Founded Ozark Mountain Construction and leads company strategy and client relationships across every OMC project nationwide.',
};

const LAURA = {
  id: 'laura-pelley',
  name: 'Laura Pelley',
  title: 'Vice President & COO',
  email: 'laura@ozarkmtnco.com',
  photo: '/team/laura-headshot-zoom.jpg',
  bio: 'Oversees daily operations, scheduling, and field execution from contract through closeout.',
};

// Administrative team roster — names/titles supplied by client. Phones hidden per client.
const ADMIN_TEAM = [
  { id: 'bryan-mashburn', name: 'Bryan Mashburn', title: 'Senior Superintendent', email: 'bryan@ozarkmtnco.com' },
  { id: 'parker-prock', name: 'Parker Prock', title: 'Sales Manager', email: 'parker@ozarkmtnco.com' },
  { id: 'mark-wallace', name: 'Mark Wallace', title: 'Senior Superintendent', email: 'markw@ozarkmtnco.com' },
  { id: 'amber-breshears', name: 'Amber Breshears', title: 'Senior Project Manager', email: 'amber@ozarkmtnco.com' },
  { id: 'aubery-wallace', name: 'Aubery Wallace', title: 'Assistant Project Manager', email: 'aubery@ozarkmtnco.com' },
  { id: 'noah-pelley', name: 'Noah Pelley', title: 'Junior Superintendent', email: 'noah@ozarkmtnco.com' },
  { id: 'blake-mashburn', name: 'Blake Mashburn', title: 'Junior Superintendent', email: 'blake@ozarkmtnco.com' },
  { id: 'peyton-dey', name: 'Peyton Dey', title: 'Contract Manager', email: 'peyton@ozarkmtnco.com' },
];

function FounderCard({ person }) {
  return (
    <div className="editorial-card overflow-hidden flex flex-col h-full" data-testid={`founder-card-${person.id}`}>
      <div className="relative overflow-hidden bg-bone-2">
        <img
          src={person.photo}
          alt={`${person.name}, ${person.title} of Ozark Mountain Construction`}
          className="w-full aspect-[3/4] object-cover object-top transition-transform duration-700 ease-out hover:scale-[1.03]"
          loading="lazy"
          data-testid={`founder-photo-${person.id}`}
        />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="kicker mb-2" data-testid={`founder-title-${person.id}`}>{person.title}</p>
        <h3 className="font-display font-semibold text-lg text-foreground mb-2 tracking-tight" data-testid={`founder-name-${person.id}`}>
          {person.name}
        </h3>
        <p className="text-[13px] text-foreground/70 leading-[1.65] mb-5">{person.bio}</p>
        <div className="mt-auto pt-4 border-t border-bone-2">
          <a
            href={`mailto:${person.email}`}
            className="flex items-center gap-2 text-[12.5px] text-foreground/80 hover:text-primary transition-colors"
            data-testid={`founder-email-${person.id}`}
          >
            <Mail className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{person.email}</span>
          </a>
        </div>
      </div>
    </div>
  );
}

export default function LeadershipTeam() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section
      id="team"
      className="relative py-20 sm:py-24 lg:py-28 bg-background"
      data-testid="leadership-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 max-w-3xl"
        >
          <p className="kicker mb-4">Leadership · Founders</p>
          <h2 className="editorial-headline text-3xl sm:text-4xl lg:text-5xl text-foreground mb-5" data-testid="leadership-headline">
            A family business,
            <span className="italic font-normal text-forest"> built to last.</span>
          </h2>
          <p className="text-foreground/75 leading-[1.75] text-[15px]">
            Ozark Mountain Construction was founded by Scott and Laura Pelley and continues
            to operate from one headquarters in Conway, Arkansas. Every project carries their
            name, and every foundation is treated with the kind of care, accountability, and
            pride that only an owner-led company can provide.
          </p>
          <p className="text-foreground/75 leading-[1.75] text-[15px] mt-4">
            From the first conversation to final closeout, OMC stays directly involved. Our
            clients are not passed from one regional representative to another. They work with
            a team that knows the job, understands the stakes, and takes personal responsibility
            for getting the foundation built right.
          </p>
        </motion.div>

        {/* Founders trio — Scott · Couple (center) · Laura */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-stretch"
          data-testid="founders-grid"
        >
          <div className="lg:col-span-3 order-2 lg:order-1">
            <FounderCard person={SCOTT} />
          </div>

          {/* Center couple feature */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="editorial-card overflow-hidden h-full flex flex-col" data-testid="founders-couple-card">
              <div className="relative overflow-hidden bg-bone-2">
                <img
                  src="/team/pelley-23.jpg"
                  alt="Scott and Laura Pelley, founders of Ozark Mountain Construction"
                  className="w-full aspect-[4/5] sm:aspect-[5/5] object-cover object-top"
                  loading="lazy"
                  data-testid="founders-couple-photo"
                />
                <div className="absolute bottom-4 left-4 bg-coal/85 text-bone backdrop-blur-sm border border-white/10 rounded-md px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] uppercase">
                  Scott &amp; Laura Pelley · Founders
                </div>
              </div>
              <div className="p-6 text-center">
                <p className="kicker mb-2 justify-center">Established 1999 · Conway, Arkansas</p>
                <p className="text-foreground/75 leading-[1.75] text-[14px] max-w-md mx-auto">
                  Two decades of experience, one shared vision, and a commitment to
                  stand behind every foundation we build.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 order-3">
            <FounderCard person={LAURA} />
          </div>
        </motion.div>

        {/* Administrative team roster (names only — no individual headshots) */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-14"
          data-testid="admin-roster"
        >
          <p className="kicker mb-6">The Administrative Team · Conway, AR</p>

          {/* Large group photo, right under Scott & Laura */}
          <div className="mb-12 max-w-5xl mx-auto" data-testid="admin-team-photo-box">
            <div className="relative rounded-xl overflow-hidden border border-bone-2 shadow-elev-2">
              <img
                src="/team/pelley-02.jpg"
                alt="The OMC administrative team with Scott and Laura Pelley at center"
                className="w-full aspect-[5/2] object-cover object-[50%_78%]"
                loading="lazy"
                data-testid="admin-team-photo"
              />
            </div>
            <p className="text-center text-foreground/70 font-mono text-[10.5px] tracking-[0.14em] uppercase mt-3 leading-relaxed max-w-3xl mx-auto" data-testid="admin-team-photo-caption">
              Left to right: Bryan Mashburn · Parker Prock · Mark Wallace · Amber Breshears · Scott Pelley · Laura Pelley · Aubery Wallace · Noah Pelley · Blake Mashburn · Peyton Dey
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-roster-grid">
            {ADMIN_TEAM.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.25 + i * 0.05 }}
                className="rounded-lg border border-bone-2 bg-white/40 p-4"
                data-testid={`admin-card-${p.id}`}
              >
                {p.title ? (
                  <p className="kicker mb-1.5" data-testid={`admin-title-${p.id}`}>{p.title}</p>
                ) : null}
                <h4 className="font-display font-semibold text-[15px] text-foreground tracking-tight mb-3" data-testid={`admin-name-${p.id}`}>
                  {p.name}
                </h4>
                <a
                  href={`mailto:${p.email}`}
                  className="flex items-center gap-2 text-[12px] text-foreground/70 hover:text-primary transition-colors"
                  data-testid={`admin-email-${p.id}`}
                >
                  <Mail className="w-3 h-3 shrink-0" />
                  <span className="truncate">{p.email}</span>
                </a>
              </motion.div>
            ))}
          </div>

          {/* Full company photos — placed under the admin names per client request */}
          <div className="mt-16" data-testid="full-team-block">
            <div className="mb-6 border-b border-bone-2 pb-4">
              <p className="kicker mb-2">Ozark Mountain Construction</p>
              <h3 className="font-display font-semibold text-2xl sm:text-3xl text-foreground tracking-tight">
                Superintendents &amp; Field Crew
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-3 max-w-2xl mx-auto">
              <figure className="group relative m-0 overflow-hidden rounded-xl border border-bone-2 shadow-elev-2" data-testid="full-team-photo-pond">
                <img
                  src="/team/team-pond-crop.jpg"
                  alt="OMC field crew and superintendents in front of the headquarters pond"
                  className="block w-full h-auto transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                  data-testid="full-team-img-pond"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0e1416] via-[#0e1416]/20 to-transparent pt-12 px-4 pb-3 font-mono text-[10px] tracking-[0.16em] uppercase text-bone/90">
                  Field Crew
                </figcaption>
              </figure>
              <figure className="group relative m-0 overflow-hidden rounded-xl border border-bone-2 shadow-elev-2" data-testid="full-team-photo-building">
                <img
                  src="/team/team-hq-crop.jpg"
                  alt="The whole Ozark Mountain Construction team at the Conway headquarters"
                  className="block w-full h-auto transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                  data-testid="full-team-img-building"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0e1416] via-[#0e1416]/20 to-transparent pt-12 px-4 pb-3 font-mono text-[10px] tracking-[0.16em] uppercase text-bone/90">
                  The Whole OMC Team · Conway Headquarters
                </figcaption>
              </figure>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
